import { Form, Select, Typography } from "antd";
import { useMemo } from "react";
import { translate } from "shared/languages";
import { ClassType, User } from "shared/types";
import { ClassCategory } from "shared/types/enum";
import { colors } from "shared/theme/color";
import {
  personFilterOption,
  renderPersonOption,
} from "shared/components/PersonSelectOption";
import { SectionEyebrow } from "./SectionEyebrow";
import { textPx } from "shared/constants/zoom";

interface ClassSectionProps {
  classes: ClassType[] | undefined;
  classLoading: boolean;
  staffs: User[] | undefined;
  staffLoading: boolean;
  selectedClass: ClassType | undefined;
  selectedCustomerIds: string[];
  onClassChange: (value: string) => void;
}

export const ClassSection = ({
  classes,
  classLoading,
  staffs,
  staffLoading,
  selectedClass,
  selectedCustomerIds,
  onClassChange,
}: ClassSectionProps) => {
  const classOptions = useMemo(() => {
    if (!classes) return [];
    const privateClasses = classes.filter(
      (cls) => cls.type === ClassCategory.Private
    );
    const openClasses = classes.filter(
      (cls) => cls.type === ClassCategory.Open
    );
    const options = [];
    if (privateClasses.length > 0) {
      options.push({
        label: "Private",
        title: "Private",
        options: privateClasses.map((cls) => ({
          label: cls.name,
          value: cls.id,
        })),
      });
    }
    if (openClasses.length > 0) {
      options.push({
        label: "Open",
        title: "Open",
        options: openClasses.map((cls) => ({ label: cls.name, value: cls.id })),
      });
    }
    return options;
  }, [classes]);

  return (
    <div style={{ marginBottom: 22 }}>
      <SectionEyebrow
        num={1}
        label={translate("schedule.drawer.class_section")}
      />
      <Form.Item
        name="class_id"
        label={translate("label.class_name")}
        rules={[
          {
            required: true,
            message: `${translate("form.error.select")} ${translate(
              "label.class_name"
            )}`,
          },
        ]}
        style={{ marginBottom: 0 }}
      >
        <Select
          placeholder={`${translate("form.placeholders.select")} ${translate(
            "class"
          )}`}
          loading={classLoading}
          onChange={onClassChange}
          showSearch
          optionFilterProp="label"
          options={classOptions}
        />
      </Form.Item>
      {selectedClass && (
        <Typography.Text
          style={{
            fontSize: textPx(12),
            marginTop: 8,
            display: "block",
            color: colors.ink3,
          }}
        >
          {`Kapasitas ${selectedClass.max_participants} pax`}
        </Typography.Text>
      )}

      <Form.Item
        name="instructor_id"
        label={translate("label.instructor_name")}
        rules={[
          {
            required: true,
            message: `${translate("form.error.select")} ${translate(
              "label.instructor_name"
            )}`,
          },
        ]}
        style={{ marginTop: 16, marginBottom: 0 }}
      >
        <Select
          disabled={!selectedClass}
          labelInValue
          showSearch
          filterOption={personFilterOption}
          optionRender={renderPersonOption}
          placeholder={`${translate("form.placeholders.select")} ${translate(
            "label.instructor_name"
          )}`}
          loading={staffLoading}
          options={staffs
            ?.filter((s) => !selectedCustomerIds.includes(s.id))
            .map((s) => ({
              label: s.full_name,
              value: s.id,
              phone: s.phone_number,
            }))}
        />
      </Form.Item>
    </div>
  );
};
